import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import momentTz from "moment-timezone";
import { useNavigate } from "react-router";
import "./AddTariffModal.css";
import toast from "react-hot-toast";
import { createTariff, createTariffVersion } from "../../store/tariffesSlice";
import CloseButton from "../CloseButton/CloseButton";
import { Switch } from "@mui/material";

const defaultAttributesArray = [
  {
    code: "AVAILABLE_PROJECT_COUNT",
    name: "Количество проектов",
    valueType: "integer",
    description: "Максимальное количество проектов в организации",
  },
  {
    code: "AVAILABLE_SPACE_MB",
    name: "Место на диске (МБ)",
    valueType: "integer",
    description: "Максимальный суммарный размер всех видео в организации",
  },
  {
    code: "AVAILABLE_USER_COUNT",
    name: "Участников РП",
    valueType: "integer",
    description: "Максимальное число участников в проекте",
  },
  {
    code: "AVAILABLE_VIEWER_COUNT",
    name: "Зрителей проектов",
    valueType: "integer",
    description: "Максимальное число внешних участников в проекте",
  },
  {
    code: "ALLOW_RELEASE_NOTES",
    name: "Разрешены Release Notes",
    valueType: "boolean",
    description: "Включение функции release notes",
  },
];

const AddTariffModal = ({ onClose = () => {}, onSubmit = () => {} }) => {
  const [activeTab, setActiveTab] = useState("project");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [tariffName, setTariffName] = useState("");
  const [tariffDescription, setTariffDescription] = useState("");
  const [tariffPricesArray, setTariffPricesArray] = useState([
    {
      periodValue: 1,
      periodUnit: "MONTH",
      price: 2000,
      currency: "RUB",
    },
    {
      periodValue: 1,
      periodUnit: "YEAR",
      price: 2000,
      currency: "RUB",
    },
  ]);
  const [tariffAttributesValues, setTariffAttributesValues] = useState({
    AVAILABLE_PROJECT_COUNT: 3,
    AVAILABLE_SPACE_MB: 1024,
    AVAILABLE_USER_COUNT: 1,
    AVAILABLE_VIEWER_COUNT: 1,
    ALLOW_RELEASE_NOTES: false,
  });
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !isLoading
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Add keyframes for spinner animation
  const spinnerStyle = document.createElement("style");
  spinnerStyle.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(spinnerStyle);

  const renderAttrRow = (attr) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          marginTop: 8,
        }}
      >
        <span style={{ color: "white" }}>{attr.name}</span>
        {attr.code === "ALLOW_RELEASE_NOTES" ? (
          <Switch
            sx={{
              "&.MuiSwitch-root .MuiSwitch-switchBase": {
                color: "#F1F7FF",
                "&.Mui-checked": {
                  "& + .MuiSwitch-track": {
                    opacity: 1,
                    backgroundColor: "#3496FF",
                  },
                },
              },

              "&.MuiSwitch-root .Mui-checked": {
                color: "#F1F7FF",
              },
              "&.MuiSwitch-root .MuiSwitch-track": {
                opacity: 0.5,
                backgroundColor: "#b7d1dc",
              },
              ".Mui-checked .MuiSwitch-track": {
                opacity: 0.5,
                backgroundColor: "red",
              },
            }}
            slotProps={{ input: { "aria-label": "controlled" } }}
            checked={tariffAttributesValues[attr.code]}
            onChange={(e) => {
              const newValues = {
                ...tariffAttributesValues,
                [attr.code]: !!e.target.checked,
              };
              setTariffAttributesValues(newValues);
            }}
          />
        ) : (
          <input
            type="number"
            readOnly={isLoading}
            value={tariffAttributesValues[attr.code]}
            style={{
              ...modalStyles.lilInput,
            }}
            onChange={(e) => {
              const newValues = {
                ...tariffAttributesValues,
                [attr.code]: +e.target.value,
              };
              setTariffAttributesValues(newValues);
            }}
          />
        )}
      </div>
    );
  };

  const handleCreateTariff = () => {
    if (tariffPricesArray.length < 2) {
      toast.error("Введите цены за месяц и за год");
      return;
    }
    const data = {
      name: tariffName,
      description: tariffDescription,
    };

    dispatch(createTariff({ tariffData: data }))
      .then((res) => {
        setIsLoading(false);
        if (!res.error) {
          const tariffId = res.payload.id;
          const tariffVersionData = {
            releasedAt: momentTz().toISOString(),
            attributes: defaultAttributesArray.map((defaultAttrubute) => ({
              code: defaultAttrubute.code,
              value: tariffAttributesValues[defaultAttrubute.code],
            })),
            prices: tariffPricesArray,
          };
          return dispatch(createTariffVersion({ tariffId, tariffVersionData }));
        } else {
          return { error: res.error, payload: res.payload };
        }
      })
      .then((res) => {
        if (!res.error) {
          onSubmit();
          onClose();
        } else {
          toast.error(res.payload.error || res.error);
        }
      });
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal} ref={modalRef}>
        <CloseButton onClose={onClose} />
        <h1 style={modalStyles.title}>Добавить тариф</h1>
        <textarea
          readOnly={isLoading}
          value={tariffName}
          style={{
            ...modalStyles.input,
            lineHeight: "20px",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            "::placeholder": {
              color: "#4a4f5c",
            },
          }}
          placeholder={"Название"}
          onChange={(e) => setTariffName(e.target.value)}
        />

        <textarea
          style={{
            ...modalStyles.input,
            lineHeight: "20px",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            "::placeholder": {
              color: "#4a4f5c",
            },
          }}
          readOnly={isLoading}
          placeholder="Описание"
          value={tariffDescription}
          onChange={(e) => setTariffDescription(e.target.value)}
        />
        <span
          style={{
            color: "white",
            marginTop: 10,
            textAlign: "center",
            width: "100%",
          }}
        >
          Атрибуты тарифа
        </span>
        {defaultAttributesArray.map((attr) => renderAttrRow(attr))}
        <span
          style={{
            color: "white",
            marginTop: 10,
            textAlign: "center",
            width: "100%",
          }}
        >
          Стоимость
        </span>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginTop: 16,
          }}
        >
          <span style={{ color: "white" }}>Месяц (руб.)</span>
          <input
            type="number"
            readOnly={isLoading}
            value={tariffPricesArray[0].price || 0}
            style={{
              ...modalStyles.lilInput,
            }}
            onChange={(e) => {
              let currentValue = tariffPricesArray[0];
              if (!currentValue || currentValue.periodUnit !== "MONTH") {
                currentValue = {
                  periodValue: 1,
                  periodUnit: "MONTH",
                  price: 2000,
                  currency: "RUB",
                };
              }
              const newValue = {
                ...currentValue,
                price: e.target.value !== "" ? +e.target.value : "",
              };
              let updatedValues = [...tariffPricesArray];
              updatedValues[0] = newValue;

              setTariffPricesArray(updatedValues);
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginTop: 8,
          }}
        >
          <span style={{ color: "white" }}>Год (руб.)</span>
          <input
            type="number"
            readOnly={isLoading}
            value={tariffPricesArray[1]?.price || 0}
            style={{
              ...modalStyles.lilInput,
            }}
            // placeholder={"Название"}
            onChange={(e) => {
              let currentValue = tariffPricesArray[1];
              if (!currentValue) {
                currentValue = {
                  periodValue: 1,
                  periodUnit: "YEAR",
                  price: 2000,
                  currency: "RUB",
                };
              }
              const newValue = {
                ...currentValue,
                price: e.target.value !== "" ? +e.target.value : "",
              };
              let updatedValues = [...tariffPricesArray];
              updatedValues[1] = newValue;

              setTariffPricesArray(updatedValues);
            }}
          />
        </div>
        <button
          onClick={() => {
            if (!isLoading) {
              setIsLoading(true);
              handleCreateTariff();
            }
          }}
          disabled={isLoading}
          style={
            isLoading
              ? modalStyles.submitButtonDisabled
              : modalStyles.submitButton
          }
        >
          {isLoading ? <span>Загрузка...</span> : "Создать"}
        </button>
      </div>
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 101,
  },
  modal: {
    width: "410px",
    backgroundColor: "#1f242e",
    borderRadius: "16px",
    minHeight: "50%",
    padding: "32px",
    position: "relative",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    scrollbarWidth: "none" /* Firefox */,
    msOverflowStyle: "none" /* IE and Edge */,
    "&::-webkit-scrollbar": {
      /* Chrome, Safari and Opera */ display: "none",
    },
  },
  contentContainer: {
    width: "100%",
    height: "500px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "32px",
  },
  title: {
    color: "#c1c4cb",
    fontSize: "24px",
    fontWeight: 600,
    fontFamily: "NT Somic",
    marginBottom: "16px",
    textAlign: "center",
  },
  tabContainer: {
    display: "flex",
    gap: "4px",
    marginBottom: "24px",
    justifyContent: "center",
    width: "400px",
  },
  tab: {
    padding: "8px 24px",
    borderRadius: "80px",
    border: "1px solid #333b4b",
    backgroundColor: "#1f242e",
    color: "#717b8e",
    fontSize: "16px",
    fontWeight: 500,
    cursor: "pointer",
    flex: 1,
    //maxWidth: "130px",
    whiteSpace: "nowrap",
    overflow: "visible",
  },
  activeTab: {
    backgroundColor: "#333b4b",
    color: "#e8f2ff",
  },
  input: {
    width: "400px",
    padding: "16px",
    paddingTop: "7px",
    fontFamily: "NT Somic",
    backgroundColor: "transparent",
    border: "1px solid #333b4b",
    borderRadius: "8px",
    color: "#717b8e",
    fontSize: "14px",
    fontWeight: 500,
    marginBottom: "8px",
    resize: "none",
    height: "36px",
    minHeight: "36px",
    maxHeight: "36px",
    lineHeight: "1.5",
    boxSizing: "border-box",
    overflowY: "auto",
  },
  lilInput: {
    width: "130px",
    padding: "16px",
    paddingTop: "4px",
    paddingBottom: "4px",
    fontFamily: "NT Somic",
    backgroundColor: "transparent",
    border: "1px solid #333b4b",
    borderRadius: "8px",
    color: "#717b8e",
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: "8px",
    resize: "none",
    height: "32px",
    minHeight: "32px",
    maxHeight: "32px",
    lineHeight: "1.5",
    boxSizing: "border-box",
    overflowY: "auto",
  },
  textarea: {
    width: "400px",
    height: "100px",
    padding: "16px",
    fontFamily: "NT Somic",
    backgroundColor: "transparent",
    border: "1px solid #333b4b",
    borderRadius: "8px",
    color: "#717b8e",
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: "8px",
    resize: "none",
    boxSizing: "border-box",
  },
  uploadArea: {
    width: "400px",
    height: "70px",
    border: "1px dashed #333b4b",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginBottom: "8px",
    boxSizing: "border-box",
  },
  uploadIcon: {
    width: "32px",
    height: "32px",
    marginBottom: "8px",
  },
  uploadText: {
    color: "#c1c4cb",
    fontSize: "16px",
    fontWeight: 500,
  },
  uploadLimit: {
    color: "#717b8e",
    fontSize: "14px",
    fontWeight: 500,
  },
  submitButton: {
    padding: "16px 32px",
    backgroundColor: "#e8f2ff",
    borderRadius: "80px",
    border: "none",
    color: "#1f242e",
    fontSize: "16px",
    fontWeight: 500,
    cursor: "pointer",
    alignSelf: "center",
    ":hover": {
      opacity: 0.9,
    },
    ":disabled": {
      opacity: 0.5,
      cursor: "default",
    },
    marginTop: "32px",
  },
  submitButtonDisabled: {
    padding: "16px 32px",
    backgroundColor: "#e8f2ff",
    borderRadius: "80px",
    border: "none",
    color: "#1f242e",
    fontSize: "16px",
    fontWeight: 500,
    cursor: "pointer",
    alignSelf: "center",
    opacity: 0.5,
    cursor: "default",
    marginTop: "32px",
  },
  versionUploadArea: {
    width: "400px",
    height: "144px",
    border: "1px dashed #333b4b",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginBottom: "16px",
    boxSizing: "border-box",
  },
  versionUploadIcon: {
    width: "32px",
    height: "32px",
    marginBottom: "16px",
  },
  versionUploadText: {
    color: "#c1c4cb",
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: "8px",
    textAlign: "center",
  },
  versionUploadSubtext: {
    color: "#717b8e",
    fontSize: "14px",
    fontWeight: 500,
    textAlign: "center",
    maxWidth: "400px",
  },
  versionLabel: {
    color: "#c1c4cb",
    fontSize: "16px",
    fontWeight: 500,
    marginTop: "8px",
  },
  loadingSpinner: {
    width: "32px",
    height: "32px",
    border: "3px solid #333b4b",
    borderTop: "3px solid #e8f2ff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },
  uploadProgress: {
    color: "#717b8e",
    fontSize: "14px",
    fontWeight: 500,
    marginTop: "8px",
  },
  versionThumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "8px",
  },
};

export default AddTariffModal;
